import { useMemo, memo, useState } from "react";
import { Box, Typography, Button, Alert, Chip } from "@mui/material";
import { DocumentScanner, Info } from "@mui/icons-material";
import { AlertCircleIcon, CheckCircle2, FileTextIcon } from "lucide-react";
import WrapperComponent from "../../emiCalculator/WrapperComponent";
import AgeRulesDialog from "./AgeRulesDialog";
import SignatureCheckDialog from "./SignatureCheckDialog";
import DocumentCategoryCard from "./DocumentCategoryCard";
import BackButton from "../../../components/buttons/BackButton";
import {
  DOCUMENTS_BY_FINANCIER,
  FINANCIER_TYPES,
} from "../constants/documents.config";

const DocumentChecklist = memo(
  ({ financierType, onAcknowledge, setActiveStep, onBack, actions }) => {
    const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
    const [ageDialogOpen, setAgeDialogOpen] = useState(false);

    const categories = useMemo(() => {
      return DOCUMENTS_BY_FINANCIER[financierType] || [];
    }, [financierType]);

    const totalDocuments = useMemo(() => {
      return categories.reduce((sum, cat) => sum + cat.documents.length, 0);
    }, [categories]);

    return (
      <WrapperComponent
        title="Document Checklist"
        subTitle="Required Documents"
        icon={<DocumentScanner sx={{ fontSize: 50 }} />}
        actions={
          actions && actions?.length > 0
            ? actions
            : [
                <BackButton
                  onClick={
                    onBack
                      ? onBack
                      : () => {
                          setActiveStep((prev) => prev - 1);
                        }
                  }
                />,
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => {
                    setActiveStep((prev) => prev + 1);
                    onAcknowledge && onAcknowledge();
                  }}
                  startIcon={<CheckCircle2 />}
                  sx={{
                    bgcolor: "#10b981",
                    color: "#ffffff",
                    fontWeight: 600,
                    py: 1,
                    "&:hover": {
                      bgcolor: "#059669",
                    },
                  }}
                >
                  I Acknowledge & Proceed
                </Button>,
              ]
        }
      >
        <Box>
          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
                flexWrap: "wrap",
              }}
            >
              <Chip
                label={
                  <span>
                    Financier: <strong>{financierType}</strong>
                  </span>
                }
                color="primary"
                variant="outlined"
                icon={<FileTextIcon />}
                sx={{ p: "4px" }}
              />
              <Chip
                label={`Total: ${totalDocuments} documents`}
                color="default"
                variant="outlined"
                sx={{ p: "4px" }}
              />
            </Box>
          </Box>

          {/* Info Alerts */}
          {financierType === FINANCIER_TYPES.AMU && (
            <Alert
              severity="info"
              icon={<Info />}
              sx={{ mb: 3, backgroundColor: "#eff6ff", color: "#1e40af" }}
              action={
                <Button
                  size="small"
                  onClick={() => setSignatureDialogOpen(true)}
                >
                  View
                </Button>
              }
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Signature Check (AMU Only):
              </Typography>
              <Typography variant="caption">
                Ensure appropriate signatures based on applicant type (Borrower,
                Co-Borrower, Guarantor).
              </Typography>
            </Alert>
          )}

          <Alert
            severity="warning"
            icon={<AlertCircleIcon />}
            sx={{
              mb: 3,
              backgroundColor: "#fffbeb",
              color: "#92400e",
              borderRadius: "16px",
            }}
            action={
              <Button size="small" onClick={() => setAgeDialogOpen(true)}>
                View
              </Button>
            }
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Age-Based Rules Apply (All Financers):
            </Typography>
            <Typography variant="caption">
              Age must be between 18-55 years. Special approval required for age
              exceptions.
            </Typography>
          </Alert>

          <Box sx={{ mb: 4 }}>
            {categories.map(
              (category) =>
                category.id !== "others" && (
                  <DocumentCategoryCard
                    key={category.category}
                    category={category.category}
                    subtext={category.subtext}
                    documents={category.documents}
                    icon={category.icon}
                  />
                ),
            )}
          </Box>

          <SignatureCheckDialog
            open={signatureDialogOpen}
            onClose={() => setSignatureDialogOpen(false)}
          />
          <AgeRulesDialog
            open={ageDialogOpen}
            onClose={() => setAgeDialogOpen(false)}
          />
        </Box>
      </WrapperComponent>
    );
  },
);

DocumentChecklist.displayName = "DocumentChecklist";

export default DocumentChecklist;
